import axios, { type AxiosInstance } from "axios"
import config from "config"
import type {
  ICreateOrganization,
  IOrganization,
  IUpdateOrganization,
} from "types/organizationTypes"
import authService from "./authService"
import type {
  IAllOrganizationsGrowthReport,
  IAllOrganizationsReport,
  IOrganizationReport,
  IOrganizationUsersReport,
} from "types/reportTypes"

class OrganizationService {
  private http: AxiosInstance

  public constructor() {
    this.http = axios.create({
      baseURL: config.apiUrl,
      headers: {
        "Content-type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    })
  }

  public getOrganizations = async (): Promise<Array<IOrganization>> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<Array<IOrganization>>("/organization", config)

    return data
  }

  public getOrganization = async (id: string) => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<IOrganization>(`/organization/${id}`, config)

    return data
  }

  public createOrganization = async (organization: ICreateOrganization): Promise<IOrganization> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.post<IOrganization>("/organization", organization, config)

    return resp.data
  }

  public updateOrganization = async (
    organizationId: string,
    organization: IUpdateOrganization,
  ): Promise<IOrganization> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.put<IOrganization>(
      `/organization/${organizationId}`,
      organization,
      config,
    )

    return resp.data
  }

  public getOrganizationReport = async (
    organizationId: string,
    period: number,
  ): Promise<IOrganizationReport> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.get<IOrganizationReport>(
      `/organization/${organizationId}/report?period=${period}`,
      config,
    )

    return resp.data
  }

  public getOrganizationUsersReport = async (
    organizationId: string,
    period: number,
  ): Promise<IOrganizationUsersReport> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const resp = await this.http.get<IOrganizationUsersReport>(
      `/organization/${organizationId}/user/report?period=${period}`,
      config,
    )

    return resp.data
  }

  public getAllOrganizationsReport = async (): Promise<IAllOrganizationsReport> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<IAllOrganizationsReport>("/report", config)

    return data
  }

  public getAllOrganizationsGrowthReport = async (): Promise<IAllOrganizationsGrowthReport> => {
    const accessToken = await authService.getAccessToken()

    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    }

    const { data } = await this.http.get<IAllOrganizationsGrowthReport>("/report/growth", config)

    return data
  }
}

const organizationService = new OrganizationService()

export default organizationService
